// apps/admin/src/features/billing/GatewayRevealModal.tsx
//
// Phase 8.2 Wave 2 Task 08 — Payment Gateway Reveal Modal.
//
// Per CONTEXT.md / API-CONTRACTS.md §POST /api/v1/admin/payment-gateways/:id/reveal
//   - Payment Gateways platform-global config surface
//   - Sensitive data masking + per-reveal audit log
//   - Reveal flow requires password confirm + auto re-mask
//
// Behaviour contract:
//   1. Modal opens with an empty password field — Reveal button disabled
//      until password length ≥ 8 (matches BE zod schema PostRevealGateway
//      Request).
//   2. On submit → POST /api/v1/admin/payment-gateways/:id/reveal with
//      `{ password, reveal_seconds: 30 }` headers `Idempotency-Key`.
//   3. Success body: `{ id, secret_value, expires_at, audit_log_id }`. We
//      render `secret_value` (full key) for `reveal_seconds` seconds then
//      AUTO RE-MASK by clearing local state. `setTimeout` is cleared on
//      unmount AND on modal close so navigating away does not leak the
//      key past the deadline.
//   4. Every successful reveal POSTed implicitly carries the audit log
//      entry (BE creates the audit_log row server-side and returns its
//      id) — this satisfies "every reveal logged" without a
//      separate FE round-trip.
//   5. Wrong password → 422 PASSWORD_INVALID → inline form error,
//      modal stays open so admin can retry. Rate-limit 429 → toast +
//      modal stays open with a cooldown hint.
//
// Test IDs (from PLAN/task-08.md <test_ids>):
//   - gateway-reveal-modal           — modal root
//   - gateway-reveal-password-input  — password field
//   - gateway-reveal-submit-btn      — submit button
//
// vg-binding: PLAN:task-08
// vg-binding: P8.D-85
// vg-binding: BR-18
// vg-binding: P8.D-139
// vg-binding: INTERFACE-STANDARDS:error-shape
// vg-binding: INTERFACE-STANDARDS:message-priority

import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  useToast,
} from '@printway/ui';

import { ApiError, apiClient } from '../../lib/apiClient.js';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Types — mirror API-CONTRACTS.md Block 2 response shape
// ---------------------------------------------------------------------------

export interface GatewayRevealResponse {
  id: string;
  secret_value: string;
  expires_at: string;
  audit_log_id: string;
}

export interface GatewayRevealModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gatewayId: string;
  gatewayName: string;
  /** Optional override — defaults to 30. Clamped to 5..60 by BE. */
  revealSeconds?: number;
}

// ---------------------------------------------------------------------------
// Idempotency-Key helper (per F-04 / — fresh per submit)
// ---------------------------------------------------------------------------

function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `reveal-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function GatewayRevealModal({
  open,
  onOpenChange,
  gatewayId,
  gatewayName,
  revealSeconds = 30,
}: GatewayRevealModalProps): JSX.Element {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const [password, setPassword] = useState('');
  const [secret, setSecret] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number>(0);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const reMaskTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clear ALL timers + clear secret. Single cleanup callable from many
  // places (auto re-mask, unmount, modal close, manual close).
  function clearReveal(): void {
    if (reMaskTimerRef.current !== null) {
      clearTimeout(reMaskTimerRef.current);
      reMaskTimerRef.current = null;
    }
    if (tickTimerRef.current !== null) {
      clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
    setSecret(null);
    setCountdown(0);
  }

  // Reset all per-session state whenever modal toggles open ↔ closed so
  // re-opens never inherit a stale password/secret/error from the
  // previous session.
  useEffect(() => {
    if (!open) {
      clearReveal();
      setPassword('');
      setInlineError(null);
    }
  }, [open]);

  // Auto re-mask + countdown ticker. Spawned when `secret` lands; torn
  // down by `clearReveal()` (also runs on unmount via the cleanup return).
  useEffect(() => {
    if (secret === null) return;
    const seconds = Math.max(5, Math.min(60, revealSeconds));
    setCountdown(seconds);
    reMaskTimerRef.current = setTimeout(() => {
      clearReveal();
      toast({
        variant: 'default',
        title: t('gateway.reveal.toast.maskedTitle'),
        description: t('gateway.reveal.toast.maskedDesc'),
      });
    }, seconds * 1000);
    tickTimerRef.current = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => {
      // Component unmount path — guarantee no leaked secret in memory.
      clearReveal();
    };
    // We intentionally re-run only when secret changes (not on every
    // revealSeconds prop tick) — the seconds value is locked at reveal
    // time, not adjustable mid-countdown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secret]);

  const revealMutation = useMutation<
    GatewayRevealResponse,
    Error,
    { password: string }
  >({
    mutationFn: async ({ password: pw }) => {
      const body = {
        password: pw,
        reveal_seconds: Math.max(5, Math.min(60, revealSeconds)),
      };
      return apiClient.post<GatewayRevealResponse>(
        `/api/v1/admin/payment-gateways/${encodeURIComponent(gatewayId)}/reveal`,
        body,
        { idempotencyKey: newIdempotencyKey() },
      );
    },
    onSuccess: (res) => {
      setSecret(res.secret_value);
      setInlineError(null);
      // Wipe password from React state immediately on success so the
      // input does not retain the credential alongside the revealed key.
      setPassword('');
      toast({
        variant: 'success',
        title: t('gateway.reveal.toast.revealedTitle'),
        description: t('gateway.reveal.toast.revealedDesc', {
          seconds: Math.max(5, Math.min(60, revealSeconds)),
        }),
      });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.code === 'PASSWORD_INVALID' || err.status === 422) {
          setInlineError(err.message || t('gateway.reveal.errors.passwordInvalid'));
          return;
        }
        if (err.code === 'RATE_LIMITED' || err.status === 429) {
          toast({
            variant: 'error',
            title: t('gateway.reveal.toast.rateLimitedTitle'),
            description: err.message || t('gateway.reveal.toast.rateLimitedDesc'),
          });
          return;
        }
        toast({
          variant: 'error',
          title: t('gateway.reveal.toast.revealFailedTitle'),
          description: err.message,
        });
        return;
      }
      toast({
        variant: 'error',
        title: t('gateway.reveal.toast.revealFailedTitle'),
        description: err.message || t('gateway.reveal.toast.networkError'),
      });
    },
  });

  const canSubmit = password.length >= 8 && !revealMutation.isPending;

  function handleClose(): void {
    clearReveal();
    onOpenChange(false);
  }

  function handleCopy(): void {
    if (!secret) return;
    void navigator.clipboard?.writeText(secret);
    toast({ variant: 'success', title: t('gateway.reveal.toast.copied') });
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // ESC / backdrop click → treat as Close (also clears secret).
        if (!next) clearReveal();
        onOpenChange(next);
      }}
      aria-label={t('gateway.reveal.ariaLabel', { gateway: gatewayName })}
    >
      <div data-testid="gateway-reveal-modal" className="flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle>{t('gateway.reveal.title', { gateway: gatewayName })}</DialogTitle>
          <p className="mt-1 text-sm text-pw-text-muted">
            {t('gateway.reveal.subtitle', {
              seconds: Math.max(5, Math.min(60, revealSeconds)),
            })}
          </p>
        </DialogHeader>

        {secret === null ? (
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) revealMutation.mutate({ password });
            }}
          >
            <label
              htmlFor="gateway-reveal-password-input"
              className="text-sm font-medium text-pw-text"
            >
              {t('gateway.reveal.passwordLabel')}
            </label>
            <Input
              id="gateway-reveal-password-input"
              data-testid="gateway-reveal-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (inlineError) setInlineError(null);
              }}
              placeholder={t('gateway.reveal.passwordPlaceholder')}
              disabled={revealMutation.isPending}
            />
            {inlineError ? (
              <p
                role="alert"
                className="text-xs font-medium text-red-600 dark:text-red-300"
                data-testid="gateway-reveal-error"
              >
                {inlineError}
              </p>
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={revealMutation.isPending}
              >
                {t('gateway.reveal.cancel')}
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit}
                data-testid="gateway-reveal-submit-btn"
              >
                {revealMutation.isPending
                  ? t('gateway.reveal.verifying')
                  : t('gateway.reveal.submit')}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-3 text-xs text-amber-800 dark:text-amber-300">
              {t('gateway.reveal.autoHidePrefix')}{' '}
              <span
                className="font-mono font-semibold"
                data-testid="gateway-reveal-countdown"
              >
                {countdown}s
              </span>
              {t('gateway.reveal.autoHideSuffix')}
            </div>
            <div className="rounded-md bg-pw-surface p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-pw-text-muted">
                {t('gateway.reveal.apiKeyLabel')}
              </p>
              <p
                className="mt-1 break-all font-mono text-sm text-pw-text"
                data-testid="gateway-reveal-secret"
              >
                {secret}
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={handleCopy}>
                {t('gateway.reveal.copy')}
              </Button>
              <Button type="button" onClick={handleClose}>
                {t('gateway.reveal.hideNow')}
              </Button>
            </DialogFooter>
          </div>
        )}
      </div>
    </Dialog>
  );
}

export default GatewayRevealModal;
